import React, { useEffect, useState, useCallback } from 'react';
import {
  fetchTenants, createTenant, fetchTenant, updateTenant, toggleTenantActive, deleteTenant,
  fetchTenantChannels, upsertTenantChannel,
  fetchTenantWorkflow, saveTenantWorkflow,
  fetchSourceIdentities, registerSourceIdentity, revokeSourceIdentity,
} from '../services/api';
import type { Tenant, User, PaginatedResponse } from '../types';

// ─── helpers ────────────────────────────────────────────────────────────────

function toSlug(v: string) {
  return v.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

const ROLE_LABEL: Record<string, string> = {
  tenant_admin: 'Admin', manager: 'Manager', marketing: 'Marketing', agent: 'Agent',
};

const ROLE_COLOR: Record<string, { bg: string; color: string }> = {
  tenant_admin: { bg: '#ede9fe', color: '#7c3aed' },
  manager:      { bg: '#dbeafe', color: '#1d4ed8' },
  agent:        { bg: '#dcfce7', color: '#166534' },
};

// ─── sub-components ─────────────────────────────────────────────────────────

const Field: React.FC<{
  label: string; value: string; onChange: (v: string) => void;
  required?: boolean; placeholder?: string; type?: string; hint?: string;
}> = ({ label, value, onChange, required, placeholder, type = 'text', hint }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: 'var(--text)' }}>
      {label}{required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}
    </label>
    <input
      type={type} className="form-input" required={required}
      placeholder={placeholder} value={value}
      onChange={e => onChange(e.target.value)}
      style={{ width: '100%' }}
    />
    {hint && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{hint}</div>}
  </div>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10, marginTop: 20 }}>
    {children}
  </div>
);

// ─── Create Modal ────────────────────────────────────────────────────────────

interface CreateModalProps {
  onClose: () => void;
  onCreated: (tenant: Tenant) => void;
}

const CreateModal: React.FC<CreateModalProps> = ({ onClose, onCreated }) => {
  const [name, setName]           = useState('');
  const [slug, setSlug]           = useState('');
  const [slugManual, setSlugManual] = useState(false);
  const [company, setCompany]     = useState('');
  const [email, setEmail]         = useState('');
  const [phone, setPhone]         = useState('');
  const [city, setCity]           = useState('');
  const [state, setState]         = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminMobile, setAdminMobile] = useState('');
  const [adminPass, setAdminPass] = useState('');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  const handleNameChange = (v: string) => {
    setName(v);
    if (!slugManual) setSlug(toSlug(v));
  };

  const handleSlugChange = (v: string) => {
    setSlug(toSlug(v));
    setSlugManual(v !== '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await createTenant({
        name, slug,
        company_name: company || undefined,
        email: email || undefined,
        phone: phone || undefined,
        city: city || undefined,
        state: state || undefined,
        admin_name: adminName,
        admin_email: adminEmail,
        admin_mobile: adminMobile || undefined,
        admin_password: adminPass,
      });
      onCreated(res.tenant);
      onClose();
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.errors) {
        const msgs = Object.values(data.errors as Record<string, string[]>).flat();
        setError(msgs.join(' '));
      } else {
        setError(data?.message ?? 'Failed to create tenant');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#fff', borderRadius: 14, width: 540, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}>
        {/* Header */}
        <div style={{ padding: '24px 28px 0', borderBottom: '1px solid var(--border)', paddingBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--navy)' }}>Onboard New Tenant</h2>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
                A Tenant Admin account will be created automatically.
              </p>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#94a3b8', lineHeight: 1, padding: 2 }}>×</button>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '4px 28px 24px' }}>
          <SectionLabel>Tenant Details</SectionLabel>

          <Field label="Organisation Name" value={name} onChange={handleNameChange} required placeholder="e.g. PharmaGrow Ltd." />

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: 'var(--text)' }}>
              Slug (URL-safe)<span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>
            </label>
            <input
              type="text" className="form-input" required
              placeholder="e.g. pharmagrow" value={slug}
              onChange={e => handleSlugChange(e.target.value)}
              style={{ width: '100%', fontFamily: 'monospace' }}
            />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>Auto-filled from name. Unique identifier — only lowercase, numbers and hyphens.</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Company Name" value={company} onChange={setCompany} placeholder="(optional)" />
            <Field label="Contact Email" value={email} onChange={setEmail} type="email" placeholder="(optional)" />
            <Field label="Phone" value={phone} onChange={setPhone} placeholder="+91 98765 43210" />
            <Field label="City" value={city} onChange={setCity} placeholder="Mumbai" />
          </div>
          <Field label="State" value={state} onChange={setState} placeholder="Maharashtra" />

          <div style={{ borderTop: '1px solid var(--border)', marginTop: 4 }} />
          <SectionLabel>First Admin User</SectionLabel>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Name" value={adminName} onChange={setAdminName} required placeholder="Rajiv Sharma" />
            <Field label="Mobile" value={adminMobile} onChange={setAdminMobile} placeholder="+91 98765 43210" />
          </div>
          <Field label="Email" value={adminEmail} onChange={setAdminEmail} required type="email" placeholder="admin@pharmagrow.in" />
          <Field label="Password" value={adminPass} onChange={setAdminPass} required type="password" placeholder="Min 6 characters" />

          {error && (
            <div style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 14 }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving} style={{ minWidth: 140 }}>
              {saving ? 'Creating…' : 'Create Tenant'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Tenant Detail Drawer ────────────────────────────────────────────────────

interface TenantDetailProps {
  tenantId: number;
  onClose: () => void;
  onUpdated: (t: Tenant) => void;
  initialEdit?: boolean;
}

const TenantDetail: React.FC<TenantDetailProps> = ({ tenantId, onClose, onUpdated, initialEdit = false }) => {
  const [tenant, setTenant]   = useState<(Tenant & { users?: User[] }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState<'info' | 'users'>('info');
  const [editing, setEditing] = useState(initialEdit);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  // Edit state mirrors tenant fields
  const [eName, setEName]         = useState('');
  const [eCompany, setECompany]   = useState('');
  const [eEmail, setEEmail]       = useState('');
  const [ePhone, setEPhone]       = useState('');
  const [eCity, setECity]         = useState('');
  const [eState, setEState]       = useState('');
  const [eAllocMethod, setEAllocMethod] = useState<'manual' | 'round_robin' | 'auto'>('manual');
  const [eSimMode, setESimMode] = useState<'auto' | 'ask'>('auto');

  const load = useCallback(() => {
    setLoading(true);
    fetchTenant(tenantId)
      .then((data: Tenant & { users?: User[] }) => {
        setTenant(data);
        setEName(data.name ?? '');
        setECompany(data.company_name ?? '');
        setEEmail(data.email ?? '');
        setEPhone(data.phone ?? '');
        setECity(data.city ?? '');
        setEState(data.state ?? '');
        setEAllocMethod(data.allocation_method ?? 'manual');
        setESimMode(data.sim_call_conversion_mode ?? 'auto');
      })
      .catch(() => setError('Failed to load tenant details'))
      .finally(() => setLoading(false));
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!tenant) return;
    setSaving(true);
    setError('');
    try {
      const updated = await updateTenant(tenantId, {
        name: eName, company_name: eCompany || undefined,
        email: eEmail || undefined, phone: ePhone || undefined,
        city: eCity || undefined, state: eState || undefined,
        allocation_method: eAllocMethod,
        sim_call_conversion_mode: eSimMode,
      });
      setTenant(prev => prev ? { ...prev, ...updated } : prev);
      onUpdated(updated);
      setEditing(false);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const InfoRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
    <div style={{ display: 'flex', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ width: 130, fontSize: 12, color: 'var(--text-muted)', flexShrink: 0, paddingTop: 2 }}>{label}</span>
      <span style={{ fontSize: 14, color: 'var(--text)', fontWeight: 500 }}>{value || <span style={{ color: '#cbd5e1' }}>—</span>}</span>
    </div>
  );

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#fff', borderRadius: 14, width: 600, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }}>
        {/* Header */}
        <div style={{ padding: '22px 26px 0', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          {loading ? (
            <div style={{ paddingBottom: 20, color: 'var(--text-muted)' }}>Loading…</div>
          ) : tenant ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--navy)' }}>{tenant.name}</h2>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20,
                      background: tenant.is_active ? '#dcfce7' : '#fee2e2',
                      color: tenant.is_active ? '#166534' : '#991b1b',
                    }}>
                      {tenant.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 3 }}>
                    <span style={{ fontFamily: 'monospace' }}>{tenant.slug}</span>
                    {tenant.company_name && tenant.company_name !== tenant.name && ` · ${tenant.company_name}`}
                  </div>
                </div>
                <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#94a3b8', lineHeight: 1 }}>×</button>
              </div>
              {/* Tabs */}
              <div style={{ display: 'flex', gap: 0 }}>
                {(['info', 'users'] as const).map(t => (
                  <button
                    key={t}
                    onClick={() => setTab(t)}
                    style={{
                      padding: '8px 18px', background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 14, fontWeight: 600,
                      color: tab === t ? '#7c3aed' : 'var(--text-muted)',
                      borderBottom: tab === t ? '2px solid #7c3aed' : '2px solid transparent',
                    }}
                  >
                    {t === 'info' ? 'Tenant Info' : `Users (${tenant.users?.length ?? '…'})`}
                  </button>
                ))}
              </div>
            </>
          ) : null}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 26px' }}>
          {error && (
            <div style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 14 }}>
              {error}
            </div>
          )}

          {!loading && tenant && tab === 'info' && (
            editing ? (
              <div>
                <SectionLabel>Edit Tenant Details</SectionLabel>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={{ gridColumn: '1/-1' }}>
                    <Field label="Organisation Name" value={eName} onChange={setEName} required />
                  </div>
                  <Field label="Company Name" value={eCompany} onChange={setECompany} placeholder="(optional)" />
                  <Field label="Contact Email" value={eEmail} onChange={setEEmail} type="email" placeholder="(optional)" />
                  <Field label="Phone" value={ePhone} onChange={setEPhone} placeholder="+91 98765 43210" />
                  <Field label="City" value={eCity} onChange={setECity} placeholder="Mumbai" />
                  <div style={{ gridColumn: '1/-1' }}>
                    <Field label="State" value={eState} onChange={setEState} placeholder="Maharashtra" />
                  </div>
                  <div style={{ gridColumn: '1/-1' }}>
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>
                        Lead Allocation Method
                      </label>
                      <div style={{ display: 'flex', gap: 16 }}>
                        {(['manual', 'round_robin'] as const).map(method => (
                          <label key={method} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                            <input
                              type="radio"
                              name="allocation_method"
                              value={method}
                              checked={eAllocMethod === method}
                              onChange={() => setEAllocMethod(method)}
                            />
                            <span style={{ fontWeight: eAllocMethod === method ? 600 : 400 }}>
                              {method === 'manual' ? 'Manual (admin assigns)' : 'Round Robin (auto-assign)'}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div style={{ gridColumn: '1/-1' }}>
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>
                        SIM Call Conversion
                      </label>
                      <div style={{ display: 'flex', gap: 16 }}>
                        {(['auto', 'ask'] as const).map(mode => (
                          <label key={mode} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                            <input
                              type="radio"
                              name="sim_call_conversion_mode"
                              value={mode}
                              checked={eSimMode === mode}
                              onChange={() => setESimMode(mode)}
                            />
                            <span style={{ fontWeight: eSimMode === mode ? 600 : 400 }}>
                              {mode === 'auto' ? 'Auto (upload on call end)' : 'Ask (prompt agent to convert)'}
                            </span>
                          </label>
                        ))}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                        Mobile dialer reads this on each heartbeat. In Ask mode the recording stays on the phone until the agent taps Convert.
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                  <button className="btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
                  <button className="btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                  <button className="btn-secondary" style={{ fontSize: 13, padding: '6px 16px' }} onClick={() => setEditing(true)}>Edit</button>
                </div>
                <InfoRow label="Organisation" value={tenant.name} />
                <InfoRow label="Company Name" value={tenant.company_name} />
                <InfoRow label="Slug" value={tenant.slug} />
                <InfoRow label="Email" value={tenant.email} />
                <InfoRow label="Phone" value={tenant.phone} />
                <InfoRow label="City" value={tenant.city} />
                <InfoRow label="State" value={tenant.state} />
                <InfoRow label="Lead Allocation" value={tenant.allocation_method === 'round_robin' ? 'Round Robin' : 'Manual'} />
                <InfoRow label="SIM Call Mode" value={tenant.sim_call_conversion_mode === 'ask' ? 'Ask (prompt agent)' : 'Auto (upload on call end)'} />
                <InfoRow label="Created" value={tenant.created_at ? new Date(tenant.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : undefined} />
                <InfoRow label="Tenant ID" value={`#${tenant.id}`} />
              </div>
            )
          )}

          {!loading && tenant && tab === 'users' && (
            <div>
              {(!tenant.users || tenant.users.length === 0) ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: 14 }}>No users found.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
                      {['Name', 'Email', 'Mobile', 'Role', 'Joined'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tenant.users!.map((u, i) => (
                      <tr key={u.id} style={{ borderBottom: i < tenant.users!.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <td style={{ padding: '10px 12px', fontWeight: 600, fontSize: 14, color: 'var(--navy)' }}>{u.name}</td>
                        <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--text-muted)' }}>{u.email}</td>
                        <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--text-muted)' }}>{u.mobile ?? '—'}</td>
                        <td style={{ padding: '10px 12px' }}>
                          <span style={{
                            fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                            ...(ROLE_COLOR[u.role] ?? { bg: '#f1f5f9', color: '#475569' }),
                            background: (ROLE_COLOR[u.role] ?? { bg: '#f1f5f9' }).bg,
                          }}>
                            {ROLE_LABEL[u.role] ?? u.role}
                          </span>
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-muted)' }}>
                          {u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Tenant Setup Modal ─────────────────────────────────────────────────────

type SetupTab = 'workflow' | 'teleobi' | 'meta' | 'whatsapp' | 'dify' | 'source_ids';

interface ChannelField { key: string; label: string; hint?: string; type?: string; }

const CHANNELS: { key: 'teleobi' | 'meta' | 'whatsapp'; label: string; color: string; fields: ChannelField[] }[] = [
  {
    key: 'teleobi', label: 'Teleobi', color: '#7c3aed',
    fields: [
      { key: 'api_token',       label: 'API Token',       hint: 'Teleobi Dashboard → Profile', type: 'password' },
      { key: 'phone_number_id', label: 'Phone Number ID', hint: 'Teleobi Dashboard → WhatsApp → Phone Numbers' },
    ],
  },
  {
    key: 'meta', label: 'Meta Ads', color: '#1877f2',
    fields: [
      { key: 'page_access_token', label: 'Page Access Token', hint: 'Long-lived token from Meta Business Suite', type: 'password' },
      { key: 'page_id',           label: 'Facebook Page ID',  hint: 'Numeric ID from Facebook Page → About' },
    ],
  },
  {
    key: 'whatsapp', label: '360dialog', color: '#25d366',
    fields: [
      { key: 'api_key',      label: 'API Key',         hint: '360dialog Dashboard', type: 'password' },
      { key: 'phone_number', label: 'Phone Number',    hint: 'e.g. +919876543210' },
    ],
  },
];

const SOURCE_TYPES = ['meta', 'web_form', 'google', 'indiamart', 'justdial', 'tradeindia', 'whatsapp'];

interface TenantSetupModalProps { tenant: Tenant; onClose: () => void; }

const TenantSetupModal: React.FC<TenantSetupModalProps> = ({ tenant, onClose }) => {
  const [tab, setTab] = useState<SetupTab>('workflow');

  // ── Workflow state ──────────────────────────────────────────────────────
  const [wf, setWf] = useState({ enabled: false, immediate_whatsapp: true, call_delay_minutes: 120, persona_ref: 'priya', wa_template_name: '', channel: 'whatsapp', webhook_url: '' });
  const [wfLoading, setWfLoading] = useState(true);
  const [wfSaving, setWfSaving]   = useState(false);
  const [wfSaved, setWfSaved]     = useState(false);
  const [wfError, setWfError]     = useState('');

  // ── Channel credentials state ───────────────────────────────────────────
  const [activeCh, setActiveCh] = useState<'teleobi' | 'meta' | 'whatsapp'>('teleobi');
  const [chValues, setChValues] = useState<Record<string, Record<string, string>>>({
    teleobi: { api_token: '', phone_number_id: '' },
    meta:    { page_access_token: '', page_id: '' },
    whatsapp: { api_key: '', phone_number: '' },
    dify:    { api_key: '', base_url: '', dataset_id: '' },
  });
  const [configured, setConfigured] = useState<Set<string>>(new Set());
  const [chLoading, setChLoading] = useState(true);
  const [chSaving, setChSaving]   = useState(false);
  const [chSaved, setChSaved]     = useState('');
  const [chError, setChError]     = useState('');

  // ── Source IDs state ────────────────────────────────────────────────────
  const [sourceIds, setSourceIds]       = useState<any[]>([]);
  const [sidLoading, setSidLoading]     = useState(true);
  const [sidSource, setSidSource]       = useState('meta');
  const [sidIdentifier, setSidIdentifier] = useState('');
  const [sidLabel, setSidLabel]         = useState('');
  const [sidSaving, setSidSaving]       = useState(false);
  const [sidError, setSidError]         = useState('');
  const [revoking, setRevoking]         = useState<number | null>(null);

  // ── Load all data on open ───────────────────────────────────────────────
  useEffect(() => {
    fetchTenantWorkflow(tenant.id)
      .then((d: any) => setWf({ enabled: !!d.enabled, immediate_whatsapp: !!d.immediate_whatsapp, call_delay_minutes: d.call_delay_minutes ?? 120, persona_ref: d.persona_ref ?? 'priya', wa_template_name: d.wa_template_name ?? '', channel: d.channel ?? 'whatsapp', webhook_url: d.webhook_url ?? '' }))
      .catch(() => {})
      .finally(() => setWfLoading(false));

    fetchTenantChannels(tenant.id)
      .then((rows: { channel: string }[]) => setConfigured(new Set(rows.map(r => r.channel))))
      .catch(() => {})
      .finally(() => setChLoading(false));

    fetchSourceIdentities(tenant.id)
      .then((rows: any[]) => setSourceIds(rows))
      .catch(() => {})
      .finally(() => setSidLoading(false));
  }, [tenant.id]);

  // ── Workflow save ───────────────────────────────────────────────────────
  const saveWorkflow = async () => {
    setWfSaving(true); setWfError(''); setWfSaved(false);
    try {
      const data = await saveTenantWorkflow(tenant.id, { ...wf, wa_template_name: wf.wa_template_name || null, webhook_url: wf.webhook_url || null });
      setWf(prev => ({ ...prev, ...data }));
      setWfSaved(true);
    } catch (e: any) { setWfError(e?.response?.data?.message ?? 'Failed to save'); }
    finally { setWfSaving(false); }
  };

  // ── Channel credentials save ────────────────────────────────────────────
  const saveCreds = async () => {
    setChSaving(true); setChError(''); setChSaved('');
    const ch = CHANNELS.find(c => c.key === activeCh)!;
    const creds: Record<string, string> = {};
    for (const f of ch.fields) {
      if (chValues[activeCh][f.key]?.trim()) creds[f.key] = chValues[activeCh][f.key].trim();
    }
    if (!Object.keys(creds).length) { setChError('Fill at least one field.'); setChSaving(false); return; }
    try {
      await upsertTenantChannel(tenant.id, activeCh, creds);
      setChSaved(activeCh);
      setConfigured(prev => { const s = new Set(prev); s.add(activeCh); return s; });
    } catch (e: any) { setChError(e?.response?.data?.message ?? 'Failed to save'); }
    finally { setChSaving(false); }
  };

  // ── Source ID register ──────────────────────────────────────────────────
  const registerSid = async () => {
    if (!sidIdentifier.trim()) { setSidError('Identifier is required.'); return; }
    setSidSaving(true); setSidError('');
    try {
      await registerSourceIdentity(tenant.id, { source: sidSource, identifier: sidIdentifier.trim(), label: sidLabel.trim() || undefined, kind: 'intrinsic' });
      const rows = await fetchSourceIdentities(tenant.id);
      setSourceIds(rows);
      setSidIdentifier(''); setSidLabel('');
    } catch (e: any) { setSidError(e?.response?.data?.message ?? 'Failed to register'); }
    finally { setSidSaving(false); }
  };

  const revokeSid = async (id: number) => {
    setRevoking(id);
    try {
      await revokeSourceIdentity(tenant.id, id);
      setSourceIds(prev => prev.map(r => r.id === id ? { ...r, revoked_at: new Date().toISOString() } : r));
    } catch {}
    finally { setRevoking(null); }
  };

  const TABS: { key: SetupTab; label: string }[] = [
    { key: 'workflow',   label: '⚙️ Workflow' },
    { key: 'teleobi',   label: '💬 Teleobi' },
    { key: 'meta',      label: '📘 Meta Ads' },
    { key: 'whatsapp',  label: '✅ 360dialog' },
    { key: 'dify',      label: '🤖 Dify AI' },
    { key: 'source_ids', label: '🔗 Source IDs' },
  ];

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#fff', borderRadius: 14, width: 580, maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.22)' }}>

        {/* Header */}
        <div style={{ padding: '20px 26px 0', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--navy)' }}>Tenant Setup</h2>
              <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
                {tenant.name} <span style={{ fontFamily: 'monospace', color: '#94a3b8' }}>#{tenant.id}</span>
              </p>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#94a3b8', lineHeight: 1 }}>×</button>
          </div>
          <div style={{ display: 'flex', gap: 0, overflowX: 'auto' }}>
            {TABS.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{
                padding: '7px 14px', background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                color: tab === t.key ? '#7c3aed' : 'var(--text-muted)',
                borderBottom: tab === t.key ? '2px solid #7c3aed' : '2px solid transparent',
              }}>{t.label}</button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 26px 24px' }}>

          {/* ── Workflow tab ── */}
          {tab === 'workflow' && (
            wfLoading ? <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</div> : (
              <div>
                <div style={{ background: '#f8f7ff', border: '1px solid #ede9fe', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--text-muted)', marginBottom: 18 }}>
                  n8n reads these settings via <code style={{ background: '#ede9fe', borderRadius: 4, padding: '1px 5px' }}>GET /api/agent/tenant-config</code> at the start of every workflow run.
                </div>

                {/* Enable toggle */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)', marginBottom: 16 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)' }}>Enable Automation</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>n8n workflow runs for every new lead</div>
                  </div>
                  <button
                    onClick={() => setWf(p => ({ ...p, enabled: !p.enabled }))}
                    style={{
                      width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', position: 'relative',
                      background: wf.enabled ? '#7c3aed' : '#e2e8f0', transition: 'background 0.2s',
                    }}
                  >
                    <span style={{
                      position: 'absolute', top: 3, width: 18, height: 18, borderRadius: '50%', background: '#fff',
                      transition: 'left 0.2s', left: wf.enabled ? 23 : 3,
                    }} />
                  </button>
                </div>

                {/* Immediate WhatsApp toggle */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)' }}>Send WhatsApp Immediately</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Send template as soon as lead arrives</div>
                  </div>
                  <button
                    onClick={() => setWf(p => ({ ...p, immediate_whatsapp: !p.immediate_whatsapp }))}
                    style={{ width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', position: 'relative', background: wf.immediate_whatsapp ? '#7c3aed' : '#e2e8f0', transition: 'background 0.2s' }}
                  >
                    <span style={{ position: 'absolute', top: 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', left: wf.immediate_whatsapp ? 23 : 3 }} />
                  </button>
                </div>

                {/* WhatsApp template text */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: 'var(--text)' }}>WhatsApp Opening Template</label>
                  <textarea
                    className="form-input"
                    rows={4}
                    value={wf.wa_template_name}
                    onChange={e => setWf(p => ({ ...p, wa_template_name: e.target.value }))}
                    placeholder="Namaste! Main Salvus Pharma se bol rahi hoon…"
                    style={{ width: '100%', resize: 'vertical', fontSize: 13 }}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>
                    This text is sent as the opening WhatsApp message to every new lead via Teleobi.
                  </div>
                </div>

                {/* Call delay */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: 'var(--text)' }}>Call Delay (minutes)</label>
                    <input type="number" className="form-input" min={0} max={1440}
                      value={wf.call_delay_minutes}
                      onChange={e => setWf(p => ({ ...p, call_delay_minutes: parseInt(e.target.value) || 0 }))}
                      style={{ width: '100%' }}
                    />
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>Wait before voice call (0 = immediate)</div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: 'var(--text)' }}>Channel</label>
                    <select className="form-input" value={wf.channel} onChange={e => setWf(p => ({ ...p, channel: e.target.value }))} style={{ width: '100%' }}>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="voice">Voice</option>
                      <option value="all">All</option>
                    </select>
                  </div>
                </div>

                {/* Persona ref */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: 'var(--text)' }}>AI Persona</label>
                  <input type="text" className="form-input"
                    value={wf.persona_ref}
                    onChange={e => setWf(p => ({ ...p, persona_ref: e.target.value }))}
                    placeholder="priya"
                    style={{ width: '100%', fontFamily: 'monospace' }}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>Matches agent_profiles.persona_ref (e.g. "priya", "raj")</div>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: 'var(--text)' }}>n8n Webhook URL</label>
                  <input type="url" className="form-input"
                    value={wf.webhook_url}
                    onChange={e => setWf(p => ({ ...p, webhook_url: e.target.value }))}
                    placeholder="https://your-n8n.cloud/webhook/wf-automation-teleobi"
                    style={{ width: '100%', fontFamily: 'monospace', fontSize: 12 }}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>Per-tenant webhook — overrides global WF_AUTOMATION_WEBHOOK_URL. Use WF-AUTOMATION-Teleobi URL for Teleobi tenants, WF-AUTOMATION URL for 360dialog tenants.</div>
                </div>

                {wfError && <div style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>{wfError}</div>}
                {wfSaved && <div style={{ background: '#dcfce7', color: '#166534', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>✓ Workflow settings saved</div>}

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button className="btn-secondary" onClick={onClose}>Close</button>
                  <button className="btn-primary" onClick={saveWorkflow} disabled={wfSaving} style={{ minWidth: 140 }}>
                    {wfSaving ? 'Saving…' : 'Save Workflow'}
                  </button>
                </div>
              </div>
            )
          )}

          {/* ── Channel credentials tabs ── */}
          {(tab === 'teleobi' || tab === 'meta' || tab === 'whatsapp') && (
            chLoading ? <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</div> : (() => {
              // sync active channel with tab
              const chKey = tab as 'teleobi' | 'meta' | 'whatsapp';
              if (activeCh !== chKey) setActiveCh(chKey);
              const chDef = CHANNELS.find(c => c.key === chKey)!;
              return (
                <div>
                  {configured.has(chKey) ? (
                    <div style={{ background: '#dcfce7', color: '#166534', borderRadius: 8, padding: '10px 14px', fontSize: 12, marginBottom: 16, border: '1px solid #bbf7d0' }}>
                      ✓ <strong>{chDef.label}</strong> credentials already stored. Submitting will overwrite.
                    </div>
                  ) : (
                    <div style={{ background: '#f8f7ff', color: 'var(--text-muted)', borderRadius: 8, padding: '10px 14px', fontSize: 12, marginBottom: 16, border: '1px solid #ede9fe' }}>
                      Credentials are encrypted at rest in <code style={{ background: '#ede9fe', borderRadius: 4, padding: '1px 5px' }}>tenant_channels</code>. n8n reads them via <code style={{ background: '#ede9fe', borderRadius: 4, padding: '1px 5px' }}>GET /api/agent/tenant-config</code>.
                    </div>
                  )}
                  {chDef.fields.map(f => (
                    <div key={f.key} style={{ marginBottom: 16 }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: 'var(--text)' }}>{f.label}</label>
                      <input
                        type={f.type ?? 'text'} className="form-input"
                        value={chValues[chKey][f.key] ?? ''}
                        onChange={e => setChValues(prev => ({ ...prev, [chKey]: { ...prev[chKey], [f.key]: e.target.value } }))}
                        placeholder={f.type === 'password' ? '••••••••••••' : `Enter ${f.label}`}
                        style={{ width: '100%', fontFamily: f.type === 'password' ? undefined : 'monospace', fontSize: 13 }}
                      />
                      {f.hint && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{f.hint}</div>}
                    </div>
                  ))}
                  {chError && <div style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>{chError}</div>}
                  {chSaved === chKey && <div style={{ background: '#dcfce7', color: '#166534', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>✓ Saved</div>}
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button className="btn-secondary" onClick={onClose}>Close</button>
                    <button className="btn-primary" onClick={saveCreds} disabled={chSaving}
                      style={{ minWidth: 130, background: chDef.color, borderColor: chDef.color }}>
                      {chSaving ? 'Saving…' : `Save ${chDef.label}`}
                    </button>
                  </div>
                </div>
              );
            })()
          )}

          {/* ── Dify AI tab ── */}
          {tab === 'dify' && (() => {
            const difyDef = { key: 'dify', label: 'Dify AI', color: '#6366f1', fields: [
              { key: 'api_key',     label: 'Dify API Key',   hint: 'Dify Dashboard → API Access → API Key', type: 'password' as const },
              { key: 'base_url',   label: 'Dify Base URL',  hint: 'e.g. https://api.dify.ai/v1' },
              { key: 'dataset_id', label: 'Knowledge Base Dataset ID', hint: 'Dify Dashboard → Knowledge → your dataset ID' },
            ]};
            const chKey = 'dify';
            const saveDify = async () => {
              setChSaving(true); setChError(''); setChSaved('');
              try {
                const vals = chValues[chKey] || {};
                const credentials: Record<string, string> = {};
                difyDef.fields.forEach(f => { if (vals[f.key]) credentials[f.key] = vals[f.key]; });
                await upsertTenantChannel(tenant.id, chKey, credentials);
                setChSaved(chKey);
              } catch (e: any) { setChError(e?.response?.data?.message || 'Save failed'); }
              finally { setChSaving(false); }
            };
            return (
              <div>
                <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#4338ca', marginBottom: 16 }}>
                  Credentials stored encrypted in <code>tenant_channels</code>. The AI agent reads them via <code style={{ background: '#e0e7ff', padding: '1px 4px', borderRadius: 3 }}>GET /api/agent/agent-config</code>.
                </div>
                {difyDef.fields.map(f => (
                  <div key={f.key} style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: 'var(--text)' }}>{f.label}</label>
                    <input
                      type={f.type || 'text'} className="form-input"
                      value={(chValues[chKey] || {})[f.key] || ''}
                      onChange={e => setChValues(p => ({ ...p, [chKey]: { ...(p[chKey] || {}), [f.key]: e.target.value } }))}
                      style={{ width: '100%', fontFamily: 'monospace', fontSize: 13 }}
                    />
                    {f.hint && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{f.hint}</div>}
                  </div>
                ))}
                {chError && <div style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>{chError}</div>}
                {chSaved === chKey && <div style={{ background: '#dcfce7', color: '#166534', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 12 }}>✓ Dify credentials saved</div>}
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button className="btn-secondary" onClick={onClose}>Close</button>
                  <button className="btn-primary" onClick={saveDify} disabled={chSaving}
                    style={{ minWidth: 130, background: difyDef.color, borderColor: difyDef.color }}>
                    {chSaving ? 'Saving…' : 'Save Dify AI'}
                  </button>
                </div>
              </div>
            );
          })()}

          {/* ── Source IDs tab ── */}
          {tab === 'source_ids' && (
            <div>
              <div style={{ background: '#f8f7ff', border: '1px solid #ede9fe', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
                Source IDs map external identifiers (Meta page_id, Google key, portal account) to this tenant. Inbound webhooks use this to route leads to the correct tenant automatically.
              </div>

              {/* Register new */}
              <div style={{ background: 'var(--surface)', borderRadius: 8, padding: '14px 16px', marginBottom: 18, border: '1px solid var(--border)' }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 12, color: 'var(--navy)' }}>Register New Source ID</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, marginBottom: 10 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 4 }}>Source</label>
                    <select className="form-input" value={sidSource} onChange={e => setSidSource(e.target.value)} style={{ width: '100%' }}>
                      {SOURCE_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 4 }}>Identifier</label>
                    <input className="form-input" value={sidIdentifier} onChange={e => setSidIdentifier(e.target.value)}
                      placeholder="e.g. 123456789012345 (Meta page_id)" style={{ width: '100%', fontFamily: 'monospace', fontSize: 13 }} />
                  </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 4 }}>Label (optional)</label>
                  <input className="form-input" value={sidLabel} onChange={e => setSidLabel(e.target.value)}
                    placeholder="e.g. Salvus Pharma Facebook Page" style={{ width: '100%', fontSize: 13 }} />
                </div>
                {sidError && <div style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 6, padding: '8px 12px', fontSize: 12, marginBottom: 10 }}>{sidError}</div>}
                <button className="btn-primary" onClick={registerSid} disabled={sidSaving} style={{ fontSize: 13 }}>
                  {sidSaving ? 'Registering…' : '+ Register'}
                </button>
              </div>

              {/* Existing list */}
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--navy)', marginBottom: 10 }}>Registered Identities</div>
              {sidLoading ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
              ) : sourceIds.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No source identities registered yet.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
                      {['Source', 'Identifier', 'Label', 'Status', ''].map(h => (
                        <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sourceIds.map((row: any) => (
                      <tr key={row.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '8px 10px' }}>
                          <span style={{ background: '#ede9fe', color: '#7c3aed', borderRadius: 4, padding: '2px 7px', fontSize: 11, fontWeight: 700 }}>{row.source}</span>
                        </td>
                        <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 12, color: 'var(--text)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.identifier}</td>
                        <td style={{ padding: '8px 10px', color: 'var(--text-muted)', fontSize: 12 }}>{row.label ?? '—'}</td>
                        <td style={{ padding: '8px 10px' }}>
                          {row.revoked_at
                            ? <span style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 4, padding: '2px 7px', fontSize: 11, fontWeight: 700 }}>Revoked</span>
                            : <span style={{ background: '#dcfce7', color: '#166534', borderRadius: 4, padding: '2px 7px', fontSize: 11, fontWeight: 700 }}>Active</span>}
                        </td>
                        <td style={{ padding: '8px 10px' }}>
                          {!row.revoked_at && (
                            <button
                              onClick={() => revokeSid(row.id)}
                              disabled={revoking === row.id}
                              style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 5, padding: '3px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                            >
                              {revoking === row.id ? '…' : 'Revoke'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────

const TenantsPage: React.FC = () => {
  const [data, setData]               = useState<PaginatedResponse<Tenant> | null>(null);
  const [loading, setLoading]         = useState(true);
  const [showCreate, setShowCreate]   = useState(false);
  const [viewId, setViewId]           = useState<number | null>(null);
  const [editId, setEditId]           = useState<number | null>(null);
  const [toggling, setToggling]       = useState<number | null>(null);
  const [credsTenant, setCredsTenant] = useState<Tenant | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Tenant | null>(null);
  const [deleting, setDeleting]         = useState(false);
  const [delError, setDelError]         = useState('');
  const [error, setError]             = useState('');

  // ── Filters & pagination ──
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatus]     = useState('');
  const [page, setPage]               = useState(1);

  const load = useCallback(() => {
    setLoading(true);
    const params: Record<string, any> = { page };
    if (search)       params.search = search;
    if (statusFilter) params.status = statusFilter;
    fetchTenants(params)
      .then(setData)
      .catch(() => setError('Failed to load tenants'))
      .finally(() => setLoading(false));
  }, [page, search, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, statusFilter]);

  const handleToggle = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    setToggling(id);
    try {
      const res = await toggleTenantActive(id);
      setData(prev => prev ? { ...prev, data: prev.data.map(t => t.id === id ? { ...t, is_active: res.is_active } : t) } : prev);
    } catch {
      setError('Failed to toggle tenant status');
    } finally {
      setToggling(null);
    }
  };

  const handleCreated = (_tenant: Tenant) => {
    load();
  };

  const handleUpdated = (updated: Tenant) => {
    setData(prev => prev ? { ...prev, data: prev.data.map(t => t.id === updated.id ? { ...t, ...updated } : t) } : prev);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true); setDelError('');
    try {
      await deleteTenant(deleteTarget.id);
      load();
      setDeleteTarget(null);
    } catch (err: any) {
      setDelError(err?.response?.data?.message ?? 'Failed to delete tenant');
    } finally { setDeleting(false); }
  };

  const tenants = data?.data ?? [];

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* Page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Tenants</h1>
          <p style={{ marginTop: 6, color: 'var(--text-muted)', fontSize: 14 }}>
            {data ? <><strong>{data.total}</strong> organisations on the platform.</> : 'All organisations on the Automately platform.'}
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowCreate(true)}>+ Onboard Tenant</button>
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="filter-input-wrap" style={{ flex: '1 1 200px', minWidth: 160 }}>
          <svg className="filter-input-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            className="filter-input"
            placeholder="Search name, company, email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select
          className="filter-input"
          style={{ minWidth: 130, paddingLeft: 10 }}
          value={statusFilter}
          onChange={e => setStatus(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        {(search || statusFilter) && (
          <button className="btn-secondary" style={{ fontSize: 13 }} onClick={() => { setSearch(''); setStatus(''); }}>
            Clear
          </button>
        )}
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 14 }}>
          {error}
        </div>
      )}

      <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
        {loading ? (
          <div className="spinner" style={{ padding: 40 }}>Loading…</div>
        ) : tenants.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)', fontSize: 15 }}>
            {search || statusFilter ? 'No tenants match your filters.' : 'No tenants yet. Click + Onboard Tenant to add the first one.'}
          </div>
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
                  {['#', 'Organisation', 'Slug', 'Email', 'Phone', 'Users', 'SIM Mode', 'Status', 'Created', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tenants.map((t, i) => (
                  <tr
                    key={t.id}
                    onClick={() => setViewId(t.id)}
                    style={{
                      borderBottom: i < tenants.length - 1 ? '1px solid var(--border)' : 'none',
                      background: '#fff',
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f8f7ff')}
                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                  >
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#94a3b8' }}>#{t.id}</span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)' }}>{t.name}</div>
                      {t.company_name && t.company_name !== t.name && (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t.company_name}</div>
                      )}
                      {(t.city || t.state) && (
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                          {[t.city, t.state].filter(Boolean).join(', ')}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 12, fontFamily: 'monospace', color: '#64748b' }}>{t.slug}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-muted)' }}>{t.email ?? '—'}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-muted)' }}>{t.phone ?? '—'}</td>
                    <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 600, color: 'var(--text)', textAlign: 'center' }}>
                      {t.users_count ?? '—'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {(() => {
                        const mode = t.sim_call_conversion_mode ?? 'auto';
                        const isAsk = mode === 'ask';
                        return (
                          <span style={{
                            display: 'inline-block',
                            padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                            background: isAsk ? '#fef3c7' : '#dbeafe',
                            color: isAsk ? '#92400e' : '#1d4ed8',
                          }}>
                            {isAsk ? 'Ask' : 'Auto'}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <button
                        onClick={e => handleToggle(e, t.id)}
                        disabled={toggling === t.id}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '4px 11px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                          border: 'none', cursor: toggling === t.id ? 'default' : 'pointer',
                          background: t.is_active ? '#dcfce7' : '#fee2e2',
                          color: t.is_active ? '#166534' : '#991b1b',
                          opacity: toggling === t.id ? 0.6 : 1,
                        }}
                      >
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: t.is_active ? '#16a34a' : '#dc2626', display: 'inline-block' }} />
                        {toggling === t.id ? '…' : t.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {t.created_at ? new Date(t.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                    <td style={{ padding: '12px 16px' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button
                          onClick={() => setEditId(t.id)}
                          style={{ background: '#dbeafe', color: '#1d4ed8', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => setCredsTenant(t)}
                          style={{ background: '#ede9fe', color: '#7c3aed', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                        >
                          🔑 Credentials
                        </button>
                        <button
                          onClick={() => { setDeleteTarget(t); setDelError(''); }}
                          style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {data && data.last_page > 1 && (
              <div className="pager">
                <span>Page {data.current_page} of {data.last_page} &middot; {data.total} total</span>
                <div className="pager-btns">
                  <button className="pager-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                  {Array.from({ length: data.last_page }, (_, i) => i + 1).map(p => (
                    <button key={p} className={`pager-btn${p === page ? ' active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                  ))}
                  <button className="pager-btn" disabled={page === data.last_page} onClick={() => setPage(p => p + 1)}>Next →</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}

      {/* View (row click) */}
      {viewId !== null && (
        <TenantDetail
          tenantId={viewId}
          onClose={() => setViewId(null)}
          onUpdated={handleUpdated}
        />
      )}

      {/* Edit (button click — opens detail in edit mode) */}
      {editId !== null && (
        <TenantDetail
          tenantId={editId}
          onClose={() => setEditId(null)}
          onUpdated={handleUpdated}
          initialEdit
        />
      )}

      {/* Channel credentials */}
      {credsTenant && (
        <TenantSetupModal
          tenant={credsTenant}
          onClose={() => setCredsTenant(null)}
        />
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={e => { if (e.target === e.currentTarget) setDeleteTarget(null); }}
        >
          <div style={{ background: '#fff', borderRadius: 12, padding: '28px 32px', width: 440, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h2 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 700, color: '#dc2626' }}>Delete Tenant</h2>
            <p style={{ fontSize: 14, color: 'var(--text)', marginBottom: 8 }}>
              Are you sure you want to delete <strong>{deleteTarget.name}</strong>?
            </p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.6 }}>
              This will permanently delete the tenant and <strong>all their users</strong>. Leads, deals and other data will remain in the system but will be unassigned.
              <br /><br />
              <strong style={{ color: '#dc2626' }}>This cannot be undone.</strong> Consider deactivating instead.
            </p>
            {delError && (
              <div style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 7, padding: '8px 12px', fontSize: 13, marginBottom: 14 }}>{delError}</div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 600, fontSize: 14, cursor: deleting ? 'default' : 'pointer', opacity: deleting ? 0.7 : 1 }}
              >
                {deleting ? 'Deleting…' : 'Yes, Delete Tenant'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantsPage;
